/**
 * Webhook events generator: emits a per-schema event-catalog extract under
 * admin/data/webhook-events/. The barrel (admin/data/webhook-events.ts) is
 * rendered by snapshots/barrels.ts and feeds both the settings UI and the
 * runtime registerWebhookHooks() consumer.
 */

import type { FormSchema, GeneratedFile, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toCamelCase, toKebabCase } from '@core-engine/utils/string.js'

// The settings singleton is admin configuration, not site content — it never
// fires webhooks (same exclusion pattern as NO_NAV_SCHEMAS).
const NO_WEBHOOK_SCHEMAS = new Set(['settings'])

export interface WebhookEventsResult {
  files: GeneratedFile[]
}

function renderExtract(
  schemaName: string,
  kind: 'entity' | 'single' | 'form',
  label: string,
  events: string[],
  settingsKey?: string
): string {
  const lines = [
    `  schema: '${schemaName}'`,
    `  kind: '${kind}'`,
    `  label: ${JSON.stringify(label)}`,
    `  events: [${events.map((event) => `'${event}'`).join(', ')}]`
  ]
  if (settingsKey) {
    lines.push(`  settingsKey: '${settingsKey}'`)
  }

  return `import type { WebhookEventSource } from '@admin/types/webhooks'

export const ${toCamelCase(schemaName)}WebhookEvents: WebhookEventSource = {
${lines.join(',\n')}
}
`
}

export function updateWebhookEvents(
  schema: Schema,
  webhookEventsDir: string,
  kind: 'entity' | 'single'
): WebhookEventsResult {
  if (NO_WEBHOOK_SCHEMAS.has(schema.name)) {
    return { files: [] }
  }

  const events =
    kind === 'single'
      ? [`${schema.name}.updated`]
      : [
          `${schema.name}.created`,
          `${schema.name}.updated`,
          `${schema.name}.deleted`,
          ...(schema.actions?.draft === true
            ? [`${schema.name}.published`, `${schema.name}.unpublished`]
            : []),
          `${schema.name}.restored`,
          `${schema.name}.reordered`
        ]

  return {
    files: [
      createGeneratedFile(
        `${webhookEventsDir}/${schema.name}.ts`,
        renderExtract(schema.name, kind, schema.label, events)
      )
    ]
  }
}

export function updateFormWebhookEvents(
  schema: FormSchema,
  webhookEventsDir: string
): WebhookEventsResult {
  const kebabName = toKebabCase(schema.name)

  return {
    files: [
      createGeneratedFile(
        `${webhookEventsDir}/${schema.name}.ts`,
        renderExtract(schema.name, 'form', schema.label, [`form.${kebabName}.submitted`], kebabName)
      )
    ]
  }
}
